import { useEffect, useRef } from 'react';
import type { SCREEN_STATE } from '../types';

interface UseScrollingHideToolbarProps {
    containerRef: React.RefObject<HTMLDivElement>;
    screenState: SCREEN_STATE;
    isShowBar: boolean;
    isSearchDrawerOpen: boolean; // 新增：搜索弹窗是否开启
    isManualScrolling: boolean; // 新增：是否处于手动跳转引起的滚动中
    onShowBar: () => void;
    onHideBar: () => void;
}

/**
 * 处理滚动方向逻辑（从 handleScroll 提取以降低认知复杂度）
 * delta > 0 向下：超过 50px 时隐藏工具栏
 * delta < 0 向上：立即显示工具栏并重置起点
 */
function applyScrollDirectionLogic(
    delta: number,
    currentScrollTop: number,
    isShowBar: boolean,
    scrollStartTopRef: { current: number },
    onShowBar: () => void,
    onHideBar: () => void
): void {
    if (delta > 0) {
        if (isShowBar && currentScrollTop - scrollStartTopRef.current > 50) {
            onHideBar();
        }
        return;
    }
    if (delta < 0) {
        if (!isShowBar) onShowBar();
        scrollStartTopRef.current = currentScrollTop;
        return;
    }
    // delta === 0 或 isShowBar 且静止：同步起点
    if (isShowBar) {
        scrollStartTopRef.current = currentScrollTop;
    }
}

/**
 * 滚动隐藏工具栏 Hook
 *
 * 功能：
 * 全屏模式下：
 * 1. 向下长滚动（>50px）时隐藏工具栏
 * 2. 向上滚动时立即展示工具栏
 * 隐藏前提：搜索弹窗未弹起且非手动跳转滚动
 *
 * 注意：disabled 状态改为实例级 Ref，避免多实例互相影响（原模块级变量的 Bug）
 */
export function useScrollingHideToolbar(props: UseScrollingHideToolbarProps) {
    const { containerRef, screenState, isShowBar, isSearchDrawerOpen, isManualScrolling, onShowBar, onHideBar } = props;
    const lastScrollTopRef = useRef<number>(0);
    const scrollStartTopRef = useRef<number>(0);
    const isManualScrollingRef = useRef(isManualScrolling);
    const isSearchDrawerOpenRef = useRef(isSearchDrawerOpen);
    // 实例级禁用标志（替代原来的模块级 DISABLE_SCROLL_HIDE_TOOLBAR，避免多实例互相影响）
    const disabledRef = useRef(false);

    // 使用 Ref 同步状态，确保滚动回调函数始终能获取到最新值，避免闭包过时问题
    useEffect(() => {
        isManualScrollingRef.current = isManualScrolling;
    }, [isManualScrolling]);

    useEffect(() => {
        isSearchDrawerOpenRef.current = isSearchDrawerOpen;
    }, [isSearchDrawerOpen]);

    useEffect(() => {
        const container = containerRef.current;
        if (!container || disabledRef.current || screenState !== 'full') {
            return;
        }

        // 初始化位置
        lastScrollTopRef.current = container.scrollTop;
        if (isShowBar) {
            scrollStartTopRef.current = container.scrollTop;
        }

        const handleScroll = () => {
            const currentScrollTop = container.scrollTop;
            const delta = currentScrollTop - lastScrollTopRef.current;

            // 检查实时状态：搜索弹窗或手动跳转时拦截隐藏逻辑
            if (isSearchDrawerOpenRef.current || isManualScrollingRef.current) {
                scrollStartTopRef.current = currentScrollTop;
                lastScrollTopRef.current = currentScrollTop;
                return;
            }

            applyScrollDirectionLogic(delta, currentScrollTop, isShowBar, scrollStartTopRef, onShowBar, onHideBar);
            lastScrollTopRef.current = currentScrollTop;
        };

        container.addEventListener('scroll', handleScroll, { passive: true });

        return () => {
            container.removeEventListener('scroll', handleScroll);
        };
    }, [containerRef, screenState, isShowBar, onShowBar, onHideBar]);

    return {
        enable: () => {
            disabledRef.current = false;
        },
        disable: () => {
            disabledRef.current = true;
        },
    };
}
